import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const RefundPage = () => {
  useCanonical('https://ecuflashservice.com/refund');
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Refund Policy | ECU Flash Service</title>
        <meta name="description" content="ECU Flash Service refund policy. Learn about our satisfaction guarantee, refund eligibility, and how to request a refund for ECU tuning services." />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-1 text-[#888] hover:text-[#00F0FF] transition text-sm font-semibold uppercase tracking-wider">
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-white/10 mb-6">
            <RefreshCw className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#888]">Policy</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Refund</span>{' '}
            <span className="text-[#00F0FF]">Policy</span>
          </h1>
          <p className="text-[#888] text-sm">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-[#0A0A0A] border border-white/5 p-8 md:p-12">
          <div className="space-y-8">
            
            {/* Satisfaction Guarantee */}
            <section>
              <div className="bg-green-500/10 border border-green-500/30 p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-green-400 mb-2">Our Satisfaction Guarantee</h2>
                    <p className="text-green-400/80">
                      At ECU Flash Service, we stand behind the quality of our work. If your modified ECU file does not perform as specified, we will rework it free of charge or provide a full refund.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">1. Overview</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                ECU Flash Service (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is committed to ensuring customer satisfaction with our ECU tuning and modification services. This Refund Policy outlines the circumstances under which refunds may be issued and the process for requesting a refund.
              </p>
              <p className="text-[#888] leading-relaxed">
                By purchasing our services, you acknowledge that you have read and agree to this Refund Policy.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">2. Refund Eligibility</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                You may be eligible for a <strong className="text-white">full refund</strong> under the following circumstances:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Service Not Completed:</strong> If we are unable to process your ECU file due to technical limitations or file incompatibility</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">File Does Not Function:</strong> If the modified file causes the vehicle to not start or run, and we cannot provide a working solution after rework</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Incorrect Modifications:</strong> If the delivered file does not include the modifications you ordered and we cannot correct it</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Duplicate Payment:</strong> If you were charged multiple times for the same service</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Processing Delays:</strong> If your order is not completed within the promised timeframe and you no longer require the service</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">3. Non-Refundable Situations</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                Refunds will <strong className="text-[#FF003C]">NOT</strong> be issued in the following situations:
              </p>
              <ul className="space-y-2 text-[#888] mb-4">
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-[#FF003C] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">File Already Downloaded:</strong> Once you have downloaded the modified file and it functions correctly as specified</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-[#FF003C] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Incorrect Information Provided:</strong> If you provided incorrect vehicle, ECU, or hardware information that resulted in an incompatible file</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-[#FF003C] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Improper Installation:</strong> Issues caused by incorrect flashing procedures, incompatible tools, or damaged reading/writing equipment</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-[#FF003C] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Change of Mind:</strong> You decided you no longer want the modification after the work has been completed</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-[#FF003C] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Third-Party Issues:</strong> Problems caused by other modifications, mechanical issues, or third-party software</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-[#FF003C] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Legal Compliance:</strong> If you cannot use the modified file due to local laws or regulations in your jurisdiction</span>
                </li>
              </ul>
              <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-4">
                <p className="text-[#FF003C]/80 text-sm">
                  <strong className="text-[#FF003C]">Note:</strong> It is your responsibility to ensure that ECU modifications are legal in your jurisdiction before purchasing our services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">4. Free Rework Policy</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                Before requesting a refund, we offer <strong className="text-[#00F0FF]">free rework</strong> on all orders:
              </p>
              <ul className="space-y-2 text-[#888] mb-4">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Unlimited Reworks:</strong> We will rework your file at no additional cost until it meets your requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">DTC Correction:</strong> Free DTC (error code) removal for 30 days after your original purchase</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Fine-Tuning:</strong> Adjustments to performance parameters within the scope of your original order</span>
                </li>
              </ul>
              <p className="text-[#888] leading-relaxed">
                We recommend utilizing our rework service before requesting a refund, as most issues can be resolved quickly.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">5. How to Request a Refund</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                To request a refund, please follow these steps:
              </p>
              <ol className="space-y-3 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#00F0FF]/20 text-[#00F0FF] flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <span><strong className="text-white">Contact Us:</strong> Email us at <a href="mailto:support@ecuflashservice.com" className="text-[#00F0FF] hover:underline">support@ecuflashservice.com</a> with the subject line &quot;Refund Request&quot;</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#00F0FF]/20 text-[#00F0FF] flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <span><strong className="text-white">Provide Order Details:</strong> Include your order number, email address used for purchase, and the date of purchase</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#00F0FF]/20 text-[#00F0FF] flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <span><strong className="text-white">Explain the Issue:</strong> Describe the reason for your refund request in detail, including any error messages or symptoms</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#00F0FF]/20 text-[#00F0FF] flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                  <span><strong className="text-white">Attach Evidence:</strong> If applicable, include screenshots, error logs, or diagnostic data that support your request</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#00F0FF]/20 text-[#00F0FF] flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                  <span><strong className="text-white">Wait for Review:</strong> Our team will review your request within 2-3 business days and respond with a decision</span>
                </li>
              </ol>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">6. Refund Processing Time</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                Once a refund is approved:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Processing Time:</strong> Refunds are processed within 5-7 business days</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Payment Method:</strong> Refunds are issued to the original payment method used for the purchase</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Bank Processing:</strong> Depending on your bank or payment provider, it may take an additional 5-10 business days for the refund to appear in your account</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">7. Partial Refunds</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                In some cases, a partial refund may be issued:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>If only part of your order could not be completed</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>If you received partial service before requesting a refund</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>If there were issues with specific modifications while others worked correctly</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">8. Chargebacks & Disputes</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                We encourage you to contact us directly before initiating a chargeback or dispute with your payment provider. We are committed to resolving issues fairly and promptly.
              </p>
              <p className="text-[#888] leading-relaxed">
                Filing a chargeback without first contacting us may result in delays and additional complications. Our support team is available to help resolve any concerns.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">9. Contact Us</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                If you have any questions about our Refund Policy or need assistance with a refund request, please contact us:
              </p>
              <div className="bg-[#1A1A1A] border border-white/10 p-6">
                <p className="text-[#888] mb-2"><strong className="text-white">Email:</strong> <a href="mailto:support@ecuflashservice.com" className="text-[#00F0FF] hover:underline">support@ecuflashservice.com</a></p>
                <p className="text-[#888] mb-2"><strong className="text-white">Contact Page:</strong> <Link to="/contact" className="text-[#00F0FF] hover:underline">ecuflashservice.com/contact</Link></p>
                <p className="text-[#888]"><strong className="text-white">Response Time:</strong> Within 24 hours on business days</p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">10. Changes to This Policy</h2>
              <p className="text-[#888] leading-relaxed">
                ECU Flash Service reserves the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to our website. We encourage you to review this policy periodically. Your continued use of our services after any changes constitutes acceptance of the updated policy.
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RefundPage;
